import React, { useEffect, useState, useMemo, useCallback } from "react";
import EmptyComponent from "./EmptyComponent";
import { useSelector } from "react-redux";
import { RiRadioButtonLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

const StackStations = () => {
  const navigate = useNavigate();

  const navigateToStackHistory = useCallback(
    (theRouteUrl) => {
      const navigateUrl = "/stack-data/" + theRouteUrl;
      navigate(navigateUrl);
    },
    [navigate]
  );

  const information = useSelector((state) => state.information);
  const informationData = information?.informationData || [];
  const informationLoading = information?.informationLoading;
  const informationError = information?.informationError;

  const stacks = useSelector((state) => state.stacks);
  const stacksLoading = stacks?.stacksLoading;
  const stacksError = stacks?.stacksError;
  const OPSGAData = stacks?.stacksData?.OPSGA || [];
  // const OPEDAData = stacks?.stacksData?.OPEDA || [];

  const [stationsData, setStationsData] = useState([]);

  const isCompany = useSelector((state) => state.login.isCompany);
  const isLoggedIn = useSelector((state) => state.login.accessToken);

  // Memoize PersianSortComparer to avoid re-creating on each render
  const PersianSortComparer = useCallback((a, b) => {
    if (a.province === b.province) {
      if (a.city === b.city) {
        return a.stationName.localeCompare(b.stationName);
      } else {
        return a.city.localeCompare(b.city);
      }
    } else {
      return a.province.localeCompare(b.province);
    }
  }, []);

  // Memoize checkOnlineOrOffline
  const checkOnlineOrOffline = useCallback((x) => {
    const todayDate = new Date();
    const lastDataDate = new Date(x);
    const differenceInMilliseconds = todayDate - lastDataDate;
    const tenMinutesInMilliseconds = 30 * 60 * 1000;
    return differenceInMilliseconds < tenMinutesInMilliseconds;
  }, []);

  const convertToJalali = useCallback((x) => {
    const A = x.slice(0, 10);
    const B = x.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR").format(C);
    return `${D} (${B})`;
  }, []);

  // Memoize the processed stations data to prevent unnecessary re-computation
  const processedStationsData = useMemo(() => {
    if (
      !Array.isArray(OPSGAData) ||
      // !Array.isArray(OPEDAData) ||
      !Array.isArray(informationData)
    ) {
      return [];
    }

    const modifyData = (data, deviceType) =>
      data
        .map((station) => {
          const specificStationInformation = informationData.find(
            (specificStation) =>
              specificStation.device === deviceType &&
              specificStation.device_id === station.device_id
          );
          return {
            ...station,
            ...specificStationInformation,
          };
        })
        .filter((station) => !station.is_test);

    const modifiedOPSGAData = modifyData(OPSGAData, "OPSGA");
    // const modifiedOPEDAData = modifyData(OPEDAData, "OPEDA");

    // Concatenate and sort the list
    const stationsList = [...modifiedOPSGAData];
    stationsList.sort(PersianSortComparer);

    return stationsList;
  }, [OPSGAData, informationData, PersianSortComparer]);

  // Effect to set stationsData when processedStationsData changes
  useEffect(() => {
    setStationsData(processedStationsData);
  }, [processedStationsData]);

  if (stacksLoading || informationLoading) {
    return <EmptyComponent />;
  }
  if (stacksError || informationError) {
    return <EmptyComponent />;
  }

  return stationsData.length > 0 ? (
    <div id="stationTable" className="my-5">
      <table
        style={{ width: "95%", overflowX: "scroll", maxWidth: "screen" }}
        className="table table-light table-striped mx-auto mx-lg-5"
      >
        <thead>
          <tr>
            {isCompany && (
              <>
                <th className="text-center px-md-3 IransansXE">وضعیت</th>
                <th className="text-center px-md-3 IransansXE">سریال</th>
                <th className="text-center px-md-3 IransansXE">دستگاه</th>
              </>
            )}
            <th className="text-center px-md-5 d-none d-md-table-cell IransansXE">
              استان
            </th>
            <th className="text-center px-md-5 d-none d-md-table-cell IransansXE">
              شهر
            </th>
            <th className="text-center px-md-3 IransansXE">نام ایستگاه</th>
            {isCompany && (
              <th className="text-center px-md-3 IransansXE">آخرین داده</th>
            )}
            {isLoggedIn && <th className="text-center px-md-5"></th>}
          </tr>
        </thead>
        <tbody>
          {stationsData.map((station) => (
            <tr key={station.slug}>
              {isCompany && (
                <td
                  className={`text-center text-large px-md-3 align-baseline ${
                    checkOnlineOrOffline(station.ccreated_datetime)
                      ? "text-success"
                      : "text-danger"
                  }`}
                >
                  <RiRadioButtonLine />
                </td>
              )}
              {isCompany && (
                <>
                  <td className="text-center px-md-3 IransansXE ss01">
                    {station.device_id}
                  </td>
                  <td className="text-center px-md-3 IransansXE ss01">
                    {station.device}
                  </td>
                </>
              )}
              <td className="text-center px-md-5 align-baseline d-none d-md-table-cell">
                {station.province}
              </td>
              <td className="text-center px-md-5 align-baseline d-none d-md-table-cell">
                {station.city}
              </td>
              <td className="text-center px-md-3 align-baseline">
                {station.stationName}
              </td>
              {isCompany && (
                <td className="text-center px-md-3 IransansXE ss01">
                  {convertToJalali(station.ccreated_datetime)}
                </td>
              )}
              {isLoggedIn && (
                <td className="text-center px-md-5 align-baseline">
                  <button
                    className="btn btn-dark btn-sm"
                    onClick={() => navigateToStackHistory(station.slug)}
                  >
                    اطلاعات ایستگاه
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <EmptyComponent />
  );
};

export default StackStations;
